import React, { useEffect, useState } from 'react';
import api from '../api';
import { 
    Users, Building2, Clock, Activity, 
    ShieldAlert, FileSignature, Download, 
    RefreshCw
} from 'lucide-react';
import { 
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
    PieChart, Pie, Cell, Legend
} from 'recharts';

const THEME = {
    card: '#1e293b', border: '#334155', textSec: '#94a3b8', 
    primary: '#3b82f6', success: '#10b981', danger: '#ef4444', warning: '#f59e0b',
    bg: '#0f172a',
    chartColors: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6']
};

const Analytics = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [timeRange, setTimeRange] = useState('30d'); // 7d, 30d, 90d

    const fetchStats = async () => {
        setLoading(true);
        try {
            // 🔥 UPDATE: Pass the time range to backend
            const res = await api.get('analytics/', { params: { range: timeRange } });
            setStats(res.data);
        } catch (err) {
            console.error("Error loading analytics", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStats();
    }, [timeRange]);

    // 🔥 NEW: Handle CSV Export
    const handleExport = () => {
        if (!stats) return;
        
        // Construct CSV Content
        const rows = [
            ["Report Generated", new Date().toLocaleString()],
            ["Time Range", timeRange.toUpperCase()],
            [],
            ["--- LIVE STATUS ---"],
            ["Headcount", stats.live.headcount],
            ["Active Companies", stats.live.companies],
            ["Hours Today", stats.live.today_hours],
            [],
            ["--- SAFETY INCIDENTS ---"],
            ["Incidents", stats.safety_incidents.incident],
            ["Near Misses", stats.safety_incidents.near_miss],
            ["Hazards", stats.safety_incidents.hazard],
            [],
            ["--- COMPLIANCE ISSUES ---"],
            ...stats.audit_insights.top_violations.map(v => [v.name, v.count]),
            [],
            ["--- CONTRACTOR ISSUES ---"],
            ...stats.audit_insights.by_company.map(c => [c.name, c.count])
        ];

        const csvContent = "data:text/csv;charset=utf-8," 
            + rows.map(e => e.join(",")).join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `Analytics_Report_${timeRange}_${new Date().toISOString().slice(0,10)}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    if (loading && !stats) return <div style={{color:'white', padding:'40px', textAlign:'center'}}>Loading Dashboard...</div>;

    // --- DATA PREPARATION ---
    const safetyData = [
        { name: 'Incidents', value: stats.safety_incidents.incident, color: THEME.danger },
        { name: 'Near Miss', value: stats.safety_incidents.near_miss, color: THEME.warning },
        { name: 'Hazards', value: stats.safety_incidents.hazard, color: THEME.primary },
    ].filter(d => d.value > 0);

    const permitData = [
        { name: 'Active', value: stats.permits.approved, color: THEME.success },
        { name: 'Pending', value: stats.permits.pending, color: THEME.warning },
        { name: 'Rejected', value: stats.permits.rejected, color: THEME.danger },
    ].filter(d => d.value > 0);

    // --- SUB-COMPONENTS ---
    const KPI = ({ title, value, icon: Icon, color }) => (
        <div style={{background: THEME.card, padding: '20px', borderRadius: '12px', border: `1px solid ${THEME.border}`, display:'flex', alignItems:'center', justifyContent:'space-between'}}>
            <div>
                <div style={{color: THEME.textSec, fontSize: '0.85rem', fontWeight:'600', marginBottom:'5px'}}>{title}</div>
                <div style={{fontSize: '1.8rem', fontWeight: 'bold', color: 'white'}}>{value}</div>
            </div>
            <div style={{background: `${color}20`, padding:'12px', borderRadius:'10px', color: color}}>
                <Icon size={24} />
            </div>
        </div>
    );

    return (
        <div className="animate-fade-in">
            {/* --- HEADER & CONTROLS --- */}
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'30px', flexWrap:'wrap', gap:'20px'}}>
                <div>
                    <h2 style={{color: 'white', margin:0, fontSize:'1.8rem'}}>Executive Overview</h2>
                    <p style={{color: THEME.textSec, margin:'5px 0 0 0'}}>Real-time site performance and safety insights.</p>
                </div>
                
                <div style={{display:'flex', gap:'10px'}}>
                    <div style={{background: THEME.card, border:`1px solid ${THEME.border}`, borderRadius:'8px', display:'flex', overflow:'hidden'}}>
                        {['7d', '30d', '90d'].map(r => (
                            <button 
                                key={r} 
                                onClick={() => setTimeRange(r)}
                                style={{
                                    background: timeRange === r ? THEME.primary : 'transparent',
                                    color: timeRange === r ? 'white' : THEME.textSec,
                                    border: 'none', padding:'8px 16px', cursor:'pointer', fontWeight:'600', fontSize:'0.85rem'
                                }}
                            >
                                {r.toUpperCase()}
                            </button>
                        ))}
                    </div>
                    <button onClick={fetchStats} style={{background: THEME.card, border:`1px solid ${THEME.border}`, color: 'white', padding:'8px', borderRadius:'8px', cursor:'pointer'}}>
                        <RefreshCw size={18} />
                    </button>
                    {/* 🔥 UPDATE: Attached handler here */}
                    <button onClick={handleExport} style={{background: THEME.success, border:'none', color: 'white', padding:'8px 16px', borderRadius:'8px', cursor:'pointer', fontWeight:'bold', display:'flex', alignItems:'center', gap:'8px'}}>
                        <Download size={18} /> Report
                    </button>
                </div>
            </div>

            {/* --- ROW 1: LIVE OPS (KPIs) --- */}
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', marginBottom: '30px'}}>
                <KPI title="Live Headcount" value={stats.live.headcount} icon={Users} color={THEME.primary} />
                <KPI title="Active Companies" value={stats.live.companies} icon={Building2} color="#8b5cf6" />
                <KPI title="Hours Worked (Today)" value={stats.live.today_hours} icon={Clock} color={THEME.success} />
                <KPI title="Total Project Hours" value={stats.project_totals.total_man_hours} icon={Activity} color={THEME.warning} />
            </div>

            {/* --- ROW 2: DETAILED CHARTS --- */}
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px', marginBottom:'30px'}}>
                
                {/* SAFETY STATS */}
                <div style={{background: THEME.card, padding:'25px', borderRadius:'12px', border: `1px solid ${THEME.border}`}}>
                    <div style={{display:'flex', justifyContent:'space-between', marginBottom:'20px'}}>
                        <h3 style={{margin:0, color:'white', fontSize:'1.1rem', display:'flex', alignItems:'center', gap:'8px'}}>
                            <ShieldAlert size={18} color={THEME.danger}/> Safety Overview
                        </h3>
                    </div>
                    <div style={{height: '250px', width: '100%'}}>
                        {safetyData.length > 0 ? (
                            <ResponsiveContainer>
                                <PieChart>
                                    <Pie
                                        data={safetyData}
                                        innerRadius={60}
                                        outerRadius={80}
                                        paddingAngle={5}
                                        dataKey="value"
                                    >
                                        {safetyData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip 
                                        contentStyle={{backgroundColor: '#0f172a', borderColor: '#334155', color: 'white'}} 
                                        itemStyle={{color: 'white'}}
                                    />
                                    <Legend verticalAlign="bottom" height={36} iconType="circle" />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <div style={{height:'100%', display:'flex', alignItems:'center', justifyContent:'center', color:THEME.textSec, fontStyle:'italic'}}>No safety data recorded</div>
                        )}
                    </div>
                </div>

                {/* AUDIT VIOLATIONS */}
                <div style={{background: THEME.card, padding:'25px', borderRadius:'12px', border: `1px solid ${THEME.border}`}}>
                    <h3 style={{margin:'0 0 20px 0', color:'white', fontSize:'1.1rem'}}>Top Compliance Issues</h3>
                    <div style={{height: '250px', width: '100%'}}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                layout="vertical"
                                data={stats.audit_insights.top_violations}
                                margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#334155" />
                                <XAxis type="number" stroke="#94a3b8" fontSize={12} />
                                <YAxis type="category" dataKey="name" stroke="white" width={100} fontSize={11} />
                                <Tooltip cursor={{fill: 'rgba(255,255,255,0.05)'}} contentStyle={{backgroundColor: '#0f172a', borderColor: '#334155'}} />
                                <Bar dataKey="count" fill={THEME.danger} radius={[0, 4, 4, 0]} barSize={20} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* --- ROW 3: PERMITS & CONTRACTORS --- */}
            <div style={{display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '20px'}}>
                
                {/* PERMITS STATUS */}
                <div style={{background: THEME.card, padding:'25px', borderRadius:'12px', border: `1px solid ${THEME.border}`}}>
                    <h3 style={{margin:'0 0 20px 0', color:'white', fontSize:'1.1rem', display:'flex', alignItems:'center', gap:'8px'}}>
                        <FileSignature size={18} color={THEME.warning}/> Permit Status
                    </h3>
                    <div style={{display:'flex', flexDirection:'column', gap:'15px'}}>
                        {permitData.map((item, i) => (
                            <div key={i} style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                                <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                                    <div style={{width:'10px', height:'10px', borderRadius:'50%', background: item.color}}></div>
                                    <span style={{color:THEME.textSec, fontSize:'0.9rem'}}>{item.name}</span>
                                </div>
                                <div style={{fontWeight:'bold', color:'white'}}>{item.value}</div>
                            </div>
                        ))}
                        {permitData.length === 0 && <div style={{color:THEME.textSec, fontStyle:'italic'}}>No active permits</div>}
                    </div>
                </div>

                {/* CONTRACTOR PERFORMANCE */}
                <div style={{background: THEME.card, padding:'25px', borderRadius:'12px', border: `1px solid ${THEME.border}`}}>
                    {/* 🔥 UPDATE: Renamed to Compliance Issues */}
                    <h3 style={{margin:'0 0 20px 0', color:'white', fontSize:'1.1rem'}}>Contractor Compliance Issues</h3>
                    <div style={{height: '200px', width: '100%'}}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={stats.audit_insights.by_company}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#334155" />
                                <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} />
                                <YAxis stroke="#94a3b8" fontSize={12} />
                                <Tooltip cursor={{fill: 'rgba(255,255,255,0.05)'}} contentStyle={{backgroundColor: '#0f172a', borderColor: '#334155'}} />
                                <Bar dataKey="count" fill={THEME.primary} radius={[4, 4, 0, 0]} barSize={40} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Analytics;